import { Divider, Stack, Typography } from "@mui/material";
import Header from "../Headers/Header";
import Product from "../Product";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import { useIsMobile } from "src/hooks/useIsMobile";
import Subheader from "../Headers/Subheader";
import { useProduct } from "src/context/ProductProvider";
import { theme } from "src/assets/theme";
import { useTranslation } from "react-i18next";
import { usePrices } from "src/context/PricesProvider";
import { useCallback, useMemo, useState } from "react";
import { Coupon, ProductPrice } from "src/api/types";
import TermsOfConditions from "../TermsOfConditions";
import ApplyCoupon from "../ApplyCoupon";
import PaymentProcessor from "../PaymentProcessor";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import ProductsDisplay from "src/components/ProductsDisplay";
import InformationArray from "../InformationArray";

const BirthControlPaymentInformation = () => {
  const isMobile = useIsMobile();
  const slug = useProduct((p) => p.slug);
  const { t } = useTranslation();
  const page = "payment-information";
  const products = usePrices();
  const [selectedProduct, setSelectedProduct] = useSelectedProduct();
  const [coupon, setCoupon] = useState<Coupon | null>(null);

  const handleProductChange = useCallback(
    (product: ProductPrice) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );

  const discountedPrice = useMemo(() => {
    if (selectedProduct == null) return "0";
    const discount = selectedProduct?.coupon?.amount ?? 0;

    return (selectedProduct.amount - discount).toLocaleString();
  }, [selectedProduct]);

  const withCouponPrice = useMemo(() => {
    if (selectedProduct == null) return "0";
    const discount = selectedProduct?.coupon?.amount ?? 0;

    const additionalDiscount = coupon?.amount ?? 0;

    const price = Math.max(
      selectedProduct?.amount - discount - additionalDiscount,
      selectedProduct.amount_limit ?? 0,
    );

    return price.toLocaleString();
  }, [coupon?.amount, selectedProduct]);

  const buttonText = useMemo(() => t(`${page}.submit`), [t]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          <InformationArray />
        </Stack>
      </Stack>
      <Stack gap={3}>
        <ProductsDisplay
          page={page}
          handleProductChange={handleProductChange}
          products={products}
          selectedProduct={selectedProduct}
          discountedPrice={discountedPrice}
        />
        <Divider />
        <Stack gap={2}>
          <ApplyCoupon coupon={coupon} setCoupon={setCoupon} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <Typography fontSize={"18px"}>
                {t(`${page}.totalIfPrescribed`)}
              </Typography>

              <Typography
                color={theme.palette.primary.main}
                fontStyle="italic"
                fontSize={"18px"}
              >
                {t(
                  `${page}.total-disclaimer`,
                  "No extra charges. Just your treatment.",
                )}
              </Typography>
            </Stack>
            <Typography fontSize={"18px"}>${withCouponPrice}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontSize={"20px"} fontWeight={700}>
            {t(`${page}.dueToday`)}
          </Typography>
          <Typography
            fontSize={"20px"}
            fontWeight={700}
            data-testid="total-price"
          >
            $0
          </Typography>
        </Stack>
        <Divider sx={{ position: "relative", top: 15 }} />
        {selectedProduct ? (
          <PaymentProcessor
            product={selectedProduct}
            coupon={coupon}
            button={buttonText}
            dueToday={0}
          />
        ) : null}
      </Stack>
      <TermsOfConditions
        page={page}
        productSlug={slug}
        isGlp1={false}
        numberOfMonths={selectedProduct ? selectedProduct.number_of_months : 1}
      />
    </Stack>
  );
};

export default BirthControlPaymentInformation;
